/**
 * Analytics wrapper. Single point of contact with PostHog so the
 * rest of the codebase can call `capture('project_created', ...)` and
 * not think about SDK shape, opt-out state, or property hygiene.
 *
 * Read ANALYTICS_PLAN.md before adding new events. Hard rules from
 * that doc, enforced here:
 *
 *   - No free text the kid typed (class names, project titles,
 *     behavior block content). Use `bucketLength` for a length bin
 *     instead.
 *   - No image / audio / pose data.
 *   - Default opt-out toggle stored in localStorage. Read on init,
 *     respected before every capture.
 *   - PostHog reaches the cloud via /ingest/* on our own domain so
 *     adblockers don't quietly drop the data.
 */
import type { PostHog } from 'posthog-js';

/** Local storage keys. Single namespace prefix so they're greppable. */
const OPT_OUT_KEY = 'bryta:analytics-opt-out';
/** Set on first init; presence on subsequent inits → `is_returning=true`. */
const SEEN_KEY = 'bryta:analytics-seen';

const POSTHOG_PROXY_HOST = '/ingest';
const POSTHOG_UI_HOST = 'https://us.posthog.com';

/**
 * Public key from the project settings in PostHog. It's a *public*
 * write-only key (PostHog calls it the "Project API key"); shipping
 * it in the bundle is the documented use. Provided at build time via
 * VITE_POSTHOG_KEY so dev builds and PR previews don't write to the
 * production project. When unset, the wrapper no-ops cleanly.
 *
 * Read lazily — `vi.stubEnv` in tests fires after this module's
 * imports resolve, so a top-level read would freeze in the
 * unstubbed value.
 */
function getPosthogKey(): string | undefined {
  return import.meta.env.VITE_POSTHOG_KEY as string | undefined;
}

let client: PostHog | null = null;
let initPromise: Promise<PostHog | null> | null = null;
let optedOut = readOptOut();

function readOptOut(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(OPT_OUT_KEY) === '1';
  } catch {
    return false;
  }
}

function writeOptOut(v: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (v) window.localStorage.setItem(OPT_OUT_KEY, '1');
    else window.localStorage.removeItem(OPT_OUT_KEY);
  } catch {
    // private mode etc — opt-out preference just doesn't persist;
    // fail open with the in-memory flag so the user's intent for
    // this session still holds.
  }
}

/** True if analytics is currently disabled (opted out, no key, or SSR). */
export function isOptedOut(): boolean {
  return optedOut || !getPosthogKey() || typeof window === 'undefined';
}

export function setOptedOut(value: boolean): void {
  optedOut = value;
  writeOptOut(value);
  // Tell PostHog so it stops/starts sending immediately, including
  // the persistent identifier cookies. `opt_out_capturing` is the
  // canonical SDK call; it also drops queued events.
  if (client) {
    if (value) client.opt_out_capturing();
    else client.opt_in_capturing();
  }
}

/**
 * One-time init. Safe to call from a module's top-level — it lazily
 * imports `posthog-js` so the SDK code is only fetched when we
 * actually have a key. The promise resolves to the client (or null
 * if disabled) so tests and the wrapper itself can await readiness.
 */
export function initAnalytics(): Promise<PostHog | null> {
  if (initPromise) return initPromise;
  const key = getPosthogKey();
  if (typeof window === 'undefined' || !key) {
    initPromise = Promise.resolve(null);
    return initPromise;
  }
  initPromise = import('posthog-js').then(({ default: posthog }) => {
    posthog.init(key, {
      api_host: POSTHOG_PROXY_HOST,
      ui_host: POSTHOG_UI_HOST,
      // Manual events only. Autocapture would ingest free text from
      // class labels and project titles — non-starter for kids.
      autocapture: false,
      // We fire studio_visited / project_opened ourselves with our
      // own properties.
      capture_pageview: false,
      // Replay would record webcam <video> frames in the DOM. Off in
      // Phase 1; revisit only with strict element masking.
      disable_session_recording: true,
      respect_dnt: true,
      // Persistence: localStorage works inside the cookie-less /p/
      // proxy (no api domain involved) and survives across the
      // sessions this app sees.
      persistence: 'localStorage',
      // No personal data collected at boot — turn off the IP and
      // referrer auto-properties' free-form values where we can.
      property_blacklist: [
        '$referrer',
        '$initial_referrer',
        '$referring_domain',
        '$initial_referring_domain',
      ],
      // Pre-register the global properties before the first capture.
      loaded: (ph) => {
        ph.register(globalProperties());
        if (optedOut) ph.opt_out_capturing();
      },
    });
    client = posthog;
    return posthog;
  });
  return initPromise;
}

/** Capture an event. No-ops when opted out; never throws. */
export function capture(event: string, props?: Record<string, unknown>): void {
  if (isOptedOut()) return;
  // The wrapper is `async` under the hood (lazy SDK import). If we
  // get a `capture` call before init resolves, queue it on the
  // promise so we don't drop the very first events of a session.
  if (!client) {
    void initAnalytics().then((ph) => {
      if (ph && !optedOut) ph.capture(event, sanitize(props));
    });
    return;
  }
  client.capture(event, sanitize(props));
}

/** Update global properties (call on route change, project open, etc). */
export function register(props: Record<string, unknown>): void {
  if (isOptedOut()) return;
  if (!client) {
    void initAnalytics().then((ph) => ph?.register(sanitize(props) ?? {}));
    return;
  }
  client.register(sanitize(props) ?? {});
}

/**
 * Defense-in-depth: even though every call site is supposed to send
 * only metadata, strip any property whose key looks like it would
 * carry a kid's free text. Cheap to do, kills a class of bug.
 */
const BANNED_KEYS = new Set([
  'title',
  'name',
  'class_name',
  'project_title',
  'description',
  'label',
  'text',
  'content',
]);

function sanitize(props?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!props) return props;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props)) {
    if (BANNED_KEYS.has(k)) continue;
    out[k] = v;
  }
  return out;
}

// ----- Property helpers ----------------------------------------------

/** Length bucket for free text — never send the string itself. */
export function bucketLength(s: string | null | undefined): string {
  const n = s?.length ?? 0;
  if (n === 0) return '0';
  if (n <= 10) return '1-10';
  if (n <= 20) return '11-20';
  return '21+';
}

/** Confidence bucket (0–1 → low/mid/high). */
export function bucketConfidence(c: number): 'low' | 'mid' | 'high' {
  if (c < 0.5) return 'low';
  if (c < 0.85) return 'mid';
  return 'high';
}

/** Accuracy bucket on a 0–1 scale. */
export function bucketAccuracy(a: number): 'low' | 'mid' | 'high' {
  if (a < 0.7) return 'low';
  if (a < 0.9) return 'mid';
  return 'high';
}

/** Bucketed training duration — keeps the metric scannable in PostHog. */
export function bucketDuration(ms: number): string {
  if (ms < 5000) return '<5s';
  if (ms < 15000) return '5-15s';
  if (ms < 30000) return '15-30s';
  if (ms < 60000) return '30-60s';
  return '60s+';
}

/** Viewport bucket for `device_form` / `viewport_bucket` global props. */
export function viewportBucket(width: number): {
  device_form: 'mobile' | 'tablet' | 'desktop';
  viewport_bucket: '<400' | '400-720' | '720-1080' | '1080+';
} {
  const v: ReturnType<typeof viewportBucket> = {
    device_form: 'mobile',
    viewport_bucket: '<400',
  };
  if (width >= 1080) {
    v.device_form = 'desktop';
    v.viewport_bucket = '1080+';
  } else if (width >= 720) {
    v.device_form = 'tablet';
    v.viewport_bucket = '720-1080';
  } else if (width >= 400) {
    v.device_form = 'mobile';
    v.viewport_bucket = '400-720';
  }
  return v;
}

/**
 * Build the global properties registered with PostHog. Pure so tests
 * can assert the shape without booting the SDK.
 */
export function globalProperties(): Record<string, unknown> {
  const w = typeof window !== 'undefined' && window.innerWidth ? window.innerWidth : 1024;
  return {
    app_version: __BUILD_SHA__,
    is_returning: isReturning(),
    ...viewportBucket(w),
  };
}

/**
 * True if this browser has seen the studio before. Cheap localStorage
 * check, set as a side-effect on first call so subsequent sessions
 * read true. Independent of PostHog's own distinct_id so we can
 * answer the question even before the SDK has loaded.
 */
export function isReturning(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const seen = window.localStorage.getItem(SEEN_KEY) === '1';
    if (!seen) window.localStorage.setItem(SEEN_KEY, '1');
    return seen;
  } catch {
    return false;
  }
}

/**
 * Hash a UUID-ish string into a short, stable, non-reversible ID for
 * `project_local_id` properties. We don't want PostHog to know the
 * Dexie UUID — that's the kid's primary key on disk.
 */
export function shortHash(input: string): string {
  // FNV-1a 32-bit. Not crypto, doesn't need to be — we only need
  // collision resistance "enough" to distinguish the few projects on
  // a single device. Tiny, dependency-free, deterministic.
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

// ----- Test hook ------------------------------------------------------

/**
 * Reset module state. Test-only: Vitest spec uses this between cases
 * so the lazy-init promise and opt-out cache don't bleed across
 * tests. Production code never calls this.
 */
export function _resetForTests(): void {
  client = null;
  initPromise = null;
  optedOut = readOptOut();
}
